import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  InputAdornment,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";

import { useIsMobile } from "hooks/useIsMobile";
import { formatQuotingUnitOfMeasure } from "utils/quotes";

import { SHIPMENT_TRACKING_LINK_BLOG_URL } from "../../constants";
import { CurrencySelect } from "../CurrencySelect";
import { EquipmentTypeSelect } from "../EquipmentTypeSelect";
import { NewTabLink } from "../NewTabLink";
import { NumberInput } from "../NumberInput";
import { PopulateCustomerRequirementsButton } from "../PopulateCustomerRequirementsButton";

import type {
  ProjectQuoteFieldsState,
  QuoteFieldsAudience,
  QuoteFieldsCommonProps,
} from "./types";

interface Props extends QuoteFieldsCommonProps<ProjectQuoteFieldsState> {
  onPopulateShipmentRequirements: VoidFunction;
}

interface LoadsFieldsProps {
  formState: ProjectQuoteFieldsState;
  onValueChanged: <V>(key: keyof ProjectQuoteFieldsState, value: V) => void;
  audience: QuoteFieldsAudience;
}

/*
The loads inputs are the one field-level difference between the two audiences. A
carrier is negotiating, so they give the minimum and maximum number of loads their
quote is valid for; a shipper is recording a booking that already happened, so they
know the single number that was awarded and both bounds take that value.
*/
function LoadsFields(props: LoadsFieldsProps) {
  const { formState, onValueChanged, audience } = props;

  if (audience === "shipper") {
    return (
      <Stack direction="row" spacing={2} mt="1rem" alignItems="center">
        <Tooltip
          title={
            <Typography variant="h5">
              How many loads of this project the carrier was booked for.
            </Typography>
          }
        >
          <Typography variant="body1">Booked For:</Typography>
        </Tooltip>
        <NumberInput
          inputLabel="Loads Booked"
          value={formState.minimumLoads}
          integerOnly
          onChange={(val) => {
            onValueChanged<number | null>("minimumLoads", val);
            onValueChanged<number | null>("maxLoads", val);
          }}
          startAdornment={<InputAdornment position="start">#</InputAdornment>}
        />
      </Stack>
    );
  }

  return (
    <Stack direction="row" spacing={2} mt="1rem" alignItems="center">
      <Tooltip
        title={
          <Typography variant="h5">
            The minimum and maximum amount of loads your quote is valid for.
            Minimum can be equal to maximum.
          </Typography>
        }
      >
        <Typography variant="body1">Quote Valid For:</Typography>
      </Tooltip>
      <NumberInput
        /*
        No min: NumberInput's underlying base-ui NumberField clamps a typed
        value into the range rather than keeping it, so a typed 0 would become
        1 and the form's own loads validation would never report it.
        */
        inputLabel="Minimum Loads"
        value={formState.minimumLoads}
        integerOnly
        onChange={(val) => onValueChanged<number | null>("minimumLoads", val)}
        startAdornment={<InputAdornment position="start">#</InputAdornment>}
      />
      <NumberInput
        inputLabel="Maximum Loads"
        value={formState.maxLoads}
        integerOnly
        onChange={(val) => onValueChanged<number | null>("maxLoads", val)}
        startAdornment={<InputAdornment position="start">#</InputAdornment>}
      />
    </Stack>
  );
}

export function MultiShipmentProjectQuoteFields(props: Props) {
  const {
    shipment,
    formState,
    onValueChanged,
    audience,
    messages,
    loading,
    onSubmit,
    submitLabel,
    enableSubmit = true,
    onPopulateShipmentRequirements,
    secondaryAction,
  } = props;

  const onEquipmentTypeChanged = (newValue: string) => {
    onValueChanged<string>("equipmentType", newValue);
  };
  const onCurrencyChanged = (newValue: string) => {
    onValueChanged<string>("currency", newValue);
  };

  const bigScreen = !useIsMobile({ maxWidth: 750 });
  const unitOfMeasure = formatQuotingUnitOfMeasure(
    shipment.info.quoting_unit_of_measure,
    false,
  );

  return (
    <>
      <Stack
        spacing={2}
        useFlexGap
        flexWrap="wrap"
        direction={bigScreen ? "row" : "column"}
        justifyContent="flex-start"
        mt="1.2rem"
        alignItems="center"
      >
        <PopulateCustomerRequirementsButton
          onClick={onPopulateShipmentRequirements}
          source={audience}
        />
        <NumberInput
          inputLabel={
            shipment.info.quoting_unit_of_measure !== "ALL_IN"
              ? `Price ${unitOfMeasure} (per load)`
              : "Price"
          }
          maxDecimals={2}
          min={0}
          value={formState.price}
          onChange={(val) => onValueChanged<number | null>("price", val)}
          startAdornment={<InputAdornment position="start">$</InputAdornment>}
        />
        <CurrencySelect
          onChange={onCurrencyChanged}
          value={formState.currency}
        />
        <TextField
          id="project-quote-first-pickup-eta"
          label="First Pickup ETA"
          type="date"
          value={formState.pickupStart}
          InputLabelProps={{
            shrink: true,
          }}
          onChange={(e) =>
            onValueChanged<string>("pickupStart", e.target.value)
          }
          required
        />
        <TextField
          id="project-quote-last-pickup-eta"
          label="Last Pickup ETA"
          type="date"
          value={formState.deliveryEta}
          InputLabelProps={{
            shrink: true,
          }}
          onChange={(e) =>
            onValueChanged<string>("deliveryEta", e.target.value)
          }
          required
        />
      </Stack>
      <Box mt=".3rem">
        <Alert severity="info">
          {audience === "carrier" ? "Quotes are" : "The rate is"}{" "}
          <b>
            <u>{unitOfMeasure} PER LOAD</u>
          </b>{" "}
          {audience === "carrier"
            ? "(exclusive of tax, unless you explicitly state otherwise)."
            : "(exclusive of tax, unless the carrier stated otherwise)."}{" "}
          {shipment.info.quoting_unit_of_measure !== "ALL_IN"
            ? "The final price will be calculated based on the delivered quantity for each load."
            : ""}
        </Alert>
      </Box>
      <LoadsFields
        formState={formState}
        onValueChanged={onValueChanged}
        audience={audience}
      />
      <Box mt="1rem" width="100%">
        <EquipmentTypeSelect
          onChange={onEquipmentTypeChanged}
          value={formState.equipmentType}
        />
      </Box>
      <Box mt="1rem">
        <TextField
          sx={{ width: "100%" }}
          onChange={(e) => onValueChanged<string>("notes", e.target.value)}
          label="Notes (Optional)"
          value={formState.notes}
        />
      </Box>
      <Stack direction="row" mt="1rem" alignItems="center">
        <Checkbox
          checked={formState.trackingLinkOffered}
          onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
            onValueChanged<boolean>("trackingLinkOffered", event.target.checked)
          }
        />
        <span>
          {audience === "carrier"
            ? "I can provide a tracking link for this shipment. "
            : "The carrier can provide a tracking link for this shipment. "}
          <NewTabLink href={SHIPMENT_TRACKING_LINK_BLOG_URL}>
            <p>What are tracking links?</p>
          </NewTabLink>
        </span>
      </Stack>
      {messages}
      <Stack sx={{ mt: "1.5rem" }}>
        {!loading ? (
          <Stack direction="row" spacing={2} alignItems="center">
            <Button
              sx={{ p: "1rem" }}
              onClick={onSubmit}
              variant="contained"
              disabled={!enableSubmit}
            >
              {submitLabel}
            </Button>
            {secondaryAction}
          </Stack>
        ) : (
          <Stack alignItems="center">
            <CircularProgress />
          </Stack>
        )}
      </Stack>
    </>
  );
}
